/**
 * Sondar config FIELD REGISTRY — the render metadata for the settings UI, mirroring
 * the Claude-Design mockup (labels, help, ranges, units, badges). Pure data + display
 * converters; no React. Consumed by the project "Customizar" screen today and reusable
 * by the future product-wide settings screen.
 *
 * Each field names a `group` + `prop` = a path into `SourcingConfig` (the canonical
 * backend units — fractions, raw model ids). `toDisplay`/`fromDisplay` translate to the
 * recruiter-legible unit the control shows (e.g. a 0.8 weight ↔ 80%). `curve`
 * (`judge.levelShape`, an array) is NOT here — the screen renders it with a dedicated
 * editor via `CURVE_FIELD`.
 *
 * SCOPE: this registry lists only the PROJECT-overridable fields — `judge.*`,
 * `retrieval.*`, `explore.*`, and the safe `budgets.*` slice. The cost-firewall rails
 * (`budgets.dailyCap`, all `cost.*` / `infra.*`) are deliberately absent: a project may
 * not override them.
 */
import type { SourcingConfig } from "@shared/labs/sourcing-config";

export type SectionId = "quality" | "explore" | "retrieval";
export type FieldGroup = "judge" | "retrieval" | "explore" | "budgets";
export type FieldKind = "slider" | "number" | "select" | "toggle" | "segmented";
/** Badges mirror the mockup: cost impact, advanced, or a learning/bandit knob. */
export type FieldBadge = "cost" | "advanced" | "learning";

export interface FieldOption {
  value: string | number;
  label: string;
}

export interface ConfigField {
  /** Sub-object of `SourcingConfig` this field lives in. */
  group: FieldGroup;
  /** Field name within `group` (a key of that sub-object). */
  prop: string;
  section: SectionId;
  label: string;
  desc: string;
  kind: FieldKind;
  min?: number;
  max?: number;
  step?: number;
  /** Suffix shown after the value (e.g. " anos", "%"). */
  unit?: string;
  /** Prefix shown before the value (e.g. "$"). */
  prefix?: string;
  /** For select/segmented controls. */
  options?: FieldOption[];
  badges?: FieldBadge[];
  /** Canonical → display (e.g. fraction → percent). Identity when omitted. */
  toDisplay?: (raw: number) => number;
  /** Display → canonical (inverse of `toDisplay`). Identity when omitted. */
  fromDisplay?: (disp: number) => number;
}

export interface ConfigSection {
  id: SectionId;
  label: string;
  /** Hidden behind the "avançado" reveal, like the mockup's Data-science section. */
  advanced?: boolean;
}

export const SECTIONS: ConfigSection[] = [
  { id: "quality", label: "Qualidade & economia" },
  { id: "retrieval", label: "Recuperação & prompt" },
  { id: "explore", label: "Motor de exploração", advanced: true },
];

/** fraction (0..1) ↔ percent (0..100). */
const asPct = {
  toDisplay: (r: number) => Math.round(r * 100),
  fromDisplay: (d: number) => d / 100,
};

/** The judge LLM options offered by the mockup (raw ids follow the repo's `-4.6`
 *  convention; `claude-sonnet-4.6` is the product default). */
const MODEL_OPTIONS: FieldOption[] = [
  { value: "claude-sonnet-4.6", label: "Claude Sonnet 4.6" },
  { value: "claude-opus-4.6", label: "Claude Opus 4.6" },
  { value: "claude-haiku-4.6", label: "Claude Haiku 4.6" },
];

/** `judge.levelShape` — the 5-band over/under-qualification curve. Rendered by a
 *  dedicated editor (5 rows of mult/cap), not a generic control. */
export const CURVE_FIELD = {
  group: "judge" as const,
  prop: "levelShape" as const,
  section: "quality" as const,
  // biome-ignore lint/security/noSecrets: UI label, not a credential
  label: "Curva de nível (sobre/subqualificação)",
  desc: "Como o score é moldado quando o candidato está acima/abaixo do nível-alvo (−2…+2). Cada faixa tem um multiplicador e um teto.",
};

export const FIELDS: ConfigField[] = [
  // ─── Qualidade & economia ──────────────────────────────────────────────────
  {
    group: "judge",
    prop: "mustHaveWeight",
    section: "quality",
    label: "Peso dos requisitos obrigatórios",
    desc: "Quanto do núcleo de evidência vem dos must-haves (o restante vem dos desejáveis).",
    kind: "slider",
    min: 50,
    max: 95,
    step: 1,
    unit: "%",
    ...asPct,
  },
  {
    group: "judge",
    prop: "eduBonus",
    section: "quality",
    label: "Bônus de prestígio de instituição",
    desc: "Boost máximo no score por uma universidade de elite.",
    kind: "slider",
    min: 0,
    max: 0.2,
    step: 0.01,
  },
  {
    group: "judge",
    prop: "workBonus",
    section: "quality",
    label: "Bônus de prestígio de empregador",
    desc: "Boost máximo por um empregador de primeira linha.",
    kind: "slider",
    min: 0,
    max: 0.2,
    step: 0.01,
  },
  {
    group: "judge",
    prop: "eduDecayTauYears",
    section: "quality",
    label: "Meia-vida do diploma",
    desc: "Anos para o valor de um diploma decair pela metade.",
    kind: "slider",
    min: 1,
    max: 15,
    step: 1,
    unit: " anos",
  },
  {
    group: "judge",
    prop: "thinProfilePrior",
    section: "quality",
    label: "Generosidade com perfis enxutos",
    desc: "Benefício da dúvida para perfis esparsos, para não enterrar bons candidatos concisos.",
    kind: "segmented",
    options: [
      { value: 0, label: "Estrito" },
      { value: 1, label: "Médio" },
      { value: 2, label: "Generoso" },
    ],
  },
  {
    group: "judge",
    prop: "fitPassScore",
    section: "quality",
    label: "Corte de match (FIT)",
    desc: "Score mínimo para um candidato contar como match real (shortlist + parada da run).",
    kind: "slider",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    group: "judge",
    prop: "model",
    section: "quality",
    label: "Modelo do juiz",
    desc: "Modelo de LLM usado no entendimento do brief e no scoring dos candidatos.",
    kind: "select",
    options: MODEL_OPTIONS,
  },
  {
    group: "judge",
    prop: "maxTokens",
    section: "quality",
    label: "Máx. de tokens por resposta",
    desc: "Teto por chamada de LLM (entendimento e scoring).",
    kind: "number",
    min: 1000,
    max: 32000,
    step: 500,
    badges: ["cost", "advanced"],
  },
  {
    group: "budgets",
    prop: "shortlistTarget",
    section: "quality",
    label: "Meta de shortlist",
    desc: "Quantos bons matches uma run de exploração coleta antes de parar.",
    kind: "slider",
    min: 20,
    max: 300,
    step: 10,
    badges: ["cost"],
  },
  {
    group: "budgets",
    prop: "exploreDefaultPages",
    section: "quality",
    label: "Páginas de exploração — padrão",
    desc: "Valor inicial do controle de páginas ao iniciar uma exploração.",
    kind: "number",
    min: 1,
    max: 200,
    step: 5,
    unit: " págs",
    badges: ["cost"],
  },
  {
    group: "budgets",
    prop: "exploreMinPages",
    section: "quality",
    label: "Páginas de exploração — mínimo",
    desc: "Piso do controle de páginas de exploração.",
    kind: "number",
    min: 1,
    max: 200,
    step: 1,
    unit: " págs",
  },
  {
    group: "budgets",
    prop: "exploreMaxPages",
    section: "quality",
    label: "Páginas de exploração — máximo",
    desc: "Teto do controle de páginas de exploração (o teto do firewall de custo da run).",
    kind: "number",
    min: 1,
    max: 200,
    step: 1,
    unit: " págs",
    badges: ["cost"],
  },
  {
    group: "budgets",
    prop: "explorePagesStep",
    section: "quality",
    label: "Páginas de exploração — passo",
    desc: "Granularidade do controle deslizante de páginas.",
    kind: "number",
    min: 1,
    max: 50,
    step: 1,
  },
  {
    group: "budgets",
    prop: "preciseDefaultPages",
    section: "quality",
    label: "Busca precisa — padrão",
    desc: "Orçamento inicial de páginas de uma busca precisa.",
    kind: "number",
    min: 1,
    max: 100,
    step: 1,
    unit: " págs",
    badges: ["cost"],
  },
  {
    group: "budgets",
    prop: "preciseMaxPages",
    section: "quality",
    label: "Busca precisa — teto",
    desc: "Máximo de páginas que uma busca precisa pode gastar.",
    kind: "number",
    min: 1,
    max: 100,
    step: 1,
    unit: " págs",
    badges: ["cost"],
  },

  // ─── Recuperação & prompt ───────────────────────────────────────────────────
  {
    group: "retrieval",
    prop: "queryVariants",
    section: "retrieval",
    label: "Variantes de query geradas",
    desc: "Quantas variantes multilíngues de busca são geradas por brief.",
    kind: "number",
    min: 1,
    max: 12,
    step: 1,
  },
  {
    group: "retrieval",
    prop: "minYearsFloor",
    section: "retrieval",
    label: "Piso do filtro de anos mínimos",
    desc: "Abaixo deste nível de experiência, não aplicar hard-filter (estimativas do provedor são ruidosas).",
    kind: "number",
    min: 0,
    max: 15,
    step: 1,
    unit: " anos",
  },
  {
    group: "retrieval",
    prop: "gapsPerCandidate",
    section: "retrieval",
    label: "Gaps mostrados por candidato",
    desc: 'Máx. de chips "verificar" exibidos em um card.',
    kind: "number",
    min: 0,
    max: 12,
    step: 1,
  },
  {
    group: "retrieval",
    prop: "judgeCompactRoles",
    section: "retrieval",
    label: "Compactação do judge — cargos",
    desc: "Máx. de cargos do histórico enviados ao juiz por perfil.",
    kind: "number",
    min: 1,
    max: 20,
    step: 1,
    badges: ["advanced"],
  },
  {
    group: "retrieval",
    prop: "judgeCompactDegrees",
    section: "retrieval",
    label: "Compactação do judge — formações",
    desc: "Máx. de formações enviadas ao juiz por perfil.",
    kind: "number",
    min: 1,
    max: 20,
    step: 1,
    badges: ["advanced"],
  },
  {
    group: "retrieval",
    // biome-ignore lint/security/noSecrets: config key, not a credential
    prop: "judgeCompactBioChars",
    section: "retrieval",
    label: "Compactação do judge — caracteres do resumo",
    desc: "Limite de caracteres do resumo (about) enviado ao juiz.",
    kind: "number",
    min: 100,
    max: 4000,
    step: 100,
    badges: ["advanced"],
  },
  {
    group: "retrieval",
    // biome-ignore lint/security/noSecrets: config key, not a credential
    prop: "judgeCompactSkills",
    section: "retrieval",
    label: "Compactação do judge — skills",
    desc: "Máx. de skills enviadas ao juiz por perfil.",
    kind: "number",
    min: 5,
    max: 100,
    step: 5,
    badges: ["advanced"],
  },
  {
    group: "retrieval",
    prop: "enrichmentEnabled",
    section: "retrieval",
    label: "Enriquecimento pelo Level Assessment",
    desc: "Injeta sinais do modelo/disciplina de LA quando um escopo é selecionado.",
    kind: "toggle",
  },
  {
    group: "retrieval",
    prop: "maxSignalsPerAxis",
    section: "retrieval",
    label: "Sinais de enriquecimento por eixo",
    desc: "Máx. de sinais de LA injetados por eixo.",
    kind: "number",
    min: 1,
    max: 12,
    step: 1,
  },

  // ─── Motor de exploração (avançado) ─────────────────────────────────────────
  {
    group: "explore",
    prop: "batchSize",
    section: "explore",
    label: "Tamanho do batch",
    desc: "Quantas variações de query são testadas por rodada.",
    kind: "number",
    min: 1,
    max: 30,
    step: 1,
  },
  {
    group: "explore",
    prop: "judgeConcurrency",
    section: "explore",
    label: "Concorrência do judge",
    desc: "Chamadas de scoring em paralelo por rodada.",
    kind: "number",
    min: 1,
    max: 32,
    step: 1,
  },
  {
    group: "explore",
    prop: "earlyStopPatience",
    section: "explore",
    label: "Paciência para parada antecipada",
    desc: "Batches secos tolerados antes de encerrar cedo.",
    kind: "number",
    min: 1,
    max: 10,
    step: 1,
  },
  {
    group: "explore",
    // biome-ignore lint/security/noSecrets: config key, not a credential
    prop: "earlyStopMinNovelFit",
    section: "explore",
    label: "Matches novos para um batch produtivo",
    desc: "Mínimo de matches inéditos para um batch contar como produtivo.",
    kind: "number",
    min: 1,
    max: 50,
    step: 1,
  },
  {
    group: "explore",
    prop: "armRatePriorMean",
    section: "explore",
    label: "Média do prior de taxa por arm",
    desc: "Expectativa inicial de matches novos por página de um arm.",
    kind: "slider",
    min: 0.1,
    max: 20,
    step: 0.1,
    badges: ["learning"],
  },
  {
    group: "explore",
    prop: "armRateForget",
    section: "explore",
    label: "Fator de esquecimento (ρ)",
    desc: "Quão rápido o posterior de taxa de um arm esquece observações antigas.",
    kind: "slider",
    min: 0.1,
    max: 1,
    step: 0.05,
    badges: ["learning"],
  },
  {
    group: "explore",
    prop: "credibleIntervalPct",
    section: "explore",
    label: "Largura do intervalo de credibilidade",
    desc: "Largura central do intervalo usado ao comparar arms.",
    kind: "slider",
    min: 50,
    max: 99,
    step: 1,
    unit: "%",
    badges: ["learning"],
  },
  {
    group: "explore",
    prop: "dormantRateFloor",
    section: "explore",
    label: "Piso de taxa para hibernar",
    desc: "Abaixo desta taxa otimista, um arm vira dormente.",
    kind: "slider",
    min: 0,
    max: 20,
    step: 0.5,
    badges: ["learning"],
  },
  {
    group: "explore",
    prop: "branchRichRate",
    section: "explore",
    label: "Taxa de pico para ramificar",
    desc: "Pico de taxa necessário para um arm ser elegível a ramificação.",
    kind: "slider",
    min: 0,
    max: 30,
    step: 0.5,
    badges: ["learning"],
  },
  {
    group: "explore",
    prop: "branchDepleteFraction",
    section: "explore",
    label: "Queda para ramificar",
    desc: "Ramifica quando a taxa atual cai abaixo desta fração do pico.",
    kind: "slider",
    min: 10,
    max: 100,
    step: 5,
    unit: "%",
    badges: ["learning"],
    ...asPct,
  },
  {
    group: "explore",
    prop: "branchChildren",
    section: "explore",
    label: "Filhos por ramificação",
    desc: "Quantos arms refinados um arm gera ao ramificar.",
    kind: "number",
    min: 1,
    max: 8,
    step: 1,
  },
  {
    group: "explore",
    prop: "depthCap",
    section: "explore",
    label: "Profundidade máxima por arm",
    desc: "Teto de páginas puxadas de um único arm.",
    kind: "number",
    min: 1,
    max: 20,
    step: 1,
  },
  {
    group: "explore",
    prop: "structuralPriorStrength",
    section: "explore",
    label: "Força do prior estrutural",
    desc: "Quanto o motor se apoia em padrões aprendidos vs. evidência fresca ao abrir uma nova linha de query.",
    kind: "slider",
    min: 0,
    max: 1,
    step: 0.05,
    badges: ["learning"],
  },
  {
    group: "explore",
    prop: "exploreProposeCount",
    section: "explore",
    label: "Novas categorias por passo de exploração",
    desc: "Máx. de categorias inéditas que um passo de exploração pode injetar.",
    kind: "number",
    min: 0,
    max: 12,
    step: 1,
  },
  {
    group: "explore",
    // biome-ignore lint/security/noSecrets: config key, not a credential
    prop: "explorePaybackHorizonPages",
    section: "explore",
    label: "Horizonte mínimo para explorar",
    desc: "Páginas restantes mínimas para valer a pena explorar uma nova categoria.",
    kind: "number",
    min: 0,
    max: 40,
    step: 1,
  },
];

/** Read a field's canonical value out of a resolved config. */
export function readField(cfg: SourcingConfig, f: ConfigField): number | string | boolean {
  return (cfg[f.group] as Record<string, number | string | boolean>)[f.prop];
}
